import { Box, CardActions, Divider, Drawer, Typography } from '@mui/material'
import { Button, IconButton, Loading } from 'components'

import { MdKeyboardArrowLeft } from 'react-icons/md'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { useItemConfigurationReplicateDrawerStore } from './itemConfigurationReplicateDrawerStore'
import { ProductConfigurationReplicateForm } from './productConfigurationReplicateForm'
import BigNumber from 'bignumber.js'
import { useOrderStore } from 'modules/orders/store'
import { yupResolver } from '@hookform/resolvers/yup'
import { Yup, stringToNumber } from 'utils'
import { loadOrderProductPrice } from 'modules/orders/hooks'
import { useState } from 'react'

const DRAWER_WIDTH = 700

const schema = Yup.object({
  newConfigurations: Yup.array().of(
    Yup.object({
      newQuantityUn: Yup.string().required(),
      // lengthField: Yup.string().required(),
      quantity: Yup.string().required(),
      particular: Yup.object({
        id: Yup.string().required(),
        name: Yup.string(),
        initRange: Yup.number().nullable(),
        endRange: Yup.number().nullable()
      }).nullable(),
      lengthField: Yup.mixed()
        .test(
          'is-positive',
          'Deve ser maior que 0!',
          (value: any, context: any) => {
            if (context?.parent?.lengthField?.content) {
              const validate: any =
                stringToNumber(value?.content) || stringToNumber(value) || 0
              return validate > 0
            }
            return true
          }
        )
        .test(
          'has-configurationId',
          'Não foi gerada configuração',
          (v: any, context: any) => {
            return !!context?.parent?.configuration?.id
          }
        )
        .test(
          'in-range',
          'Deve estar dentro da faixa de valores',
          (v: any, context: any) => {
            const parent = context?.parent

            if (parent?.lengthField) return true

            const initRange = parent?.particular?.initRange
            const endRange = parent?.particular?.endRange

            if (
              !initRange ||
              initRange == null ||
              !endRange ||
              endRange == null
            )
              return true
            if (!!initRange && !!endRange) {
              console.log(!!initRange, !!endRange)
              const value = stringToNumber(v?.content) || stringToNumber(v) || 0

              return value >= initRange && value <= endRange
            }
            return true
          }
        )
    })
  )
})

export const ItemConfigurationReplicateDrawer = () => {
  const [isLoading, setIsLoading] = useState(false)
  const setIsItemDrawerOpen =
    useItemConfigurationReplicateDrawerStore.useSetIsDrawerOpen()
  const isItemDrawerOpen =
    useItemConfigurationReplicateDrawerStore.useIsDrawerOpen()
  const setIsDrawerOpen =
    useItemConfigurationReplicateDrawerStore.useSetIsDrawerOpen()
  const form = useOrderStore.useForm()
  const sequence = useOrderStore.useSequence()
  const addProductForm = useOrderStore.useAddProductForm()
  const product = useItemConfigurationReplicateDrawerStore.useProduct()

  const { watch: orderWatch } = useFormContext()

  const methods = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: {
      newConfigurations: [
        { newQuantityUn: '', lengthField: null, quantity: '' }
      ]
    }
  })

  const { formState, handleSubmit } = methods
  const { errors } = formState

  const isError = !!Object.keys(errors).length

  const handleBack = () => {
    setIsItemDrawerOpen(false)
  }

  const [paymentTerm, customer, seller, unity, orderType] = orderWatch([
    'paymentTerm',
    'customer',
    'seller',
    'unity',
    'orderType'
  ])

  const onSubmit = async (data: any) => {
    const {
      ipiPercent,
      icmsPercent,
      stPercent,
      discountPercent,
      salePrice,
      origin
    } = product
    setIsLoading(true)

    try {
      await data.newConfigurations.reduce(
        async (memo: any, newConfiguration: any) => {
          const results = await memo

          const configurationId = newConfiguration?.configuration?.id
          const uuid = newConfiguration.uuid

          const quantity = Number(newConfiguration.quantity)
          const icmsValue = BigNumber(salePrice)
            .times(icmsPercent)
            .times(0.01)
            .times(quantity)
            .toNumber()
          const ipiValue = BigNumber(salePrice)
            .times(ipiPercent)
            .times(0.01)
            .times(quantity)
            .toNumber()
          const stValue = BigNumber(salePrice)
            .times(stPercent)
            .times(0.01)
            .times(quantity)
            .toNumber()
          const discount = BigNumber(salePrice)
            .times(discountPercent)
            .times(0.01)
            .times(quantity)
            .toNumber()

          const productPriceResponse = await loadOrderProductPrice({
            sequence: sequence[uuid],
            productId: product.product.id,
            configurationId,
            quantity: quantity,
            origin: Number(origin),
            orderId: form?.orderId || '',
            customerId: customer?.id,
            paymentTermId: paymentTerm?.id,
            sellerId: seller?.id,
            unityId: unity?.id,
            saleTypeId: orderType?.id
          })

          const newProduct = {
            ...product,
            ...productPriceResponse.data,
            uuid,
            quantity,
            icmsValue,
            ipiValue,
            stValue,
            discount,
            configurationId,
            sequence: sequence[uuid],
            quantityUn: newConfiguration.newQuantityUn,
            configurationFormValues:
              newConfiguration?.configuration?.newConfigurationValues,
            configurationDescription:
              newConfiguration?.configuration?.description
          }
          addProductForm(newProduct)

          return [...results, newProduct]
        },
        []
      )

      setIsDrawerOpen(false)
    } catch (e) {
      console.error(e)
    }
    setIsLoading(false)
  }

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            height: '100vh',
            width: DRAWER_WIDTH,
            zIndex: 9999,
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Loading />
        </div>
      )}
      <Drawer
        anchor="right"
        open={isItemDrawerOpen}
        // onClose={() => setIsItemDrawerOpen(false)}
        keepMounted
        variant="temporary"
      >
        <Box
          sx={{
            width: { xs: '100%', sm: DRAWER_WIDTH },
            height: '100vh',
            backgroundColor: 'white',
            paddingBottom: 16,
            overflowY: 'scroll'
          }}
        >
          <div
            style={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              padding: 12,
              zIndex: 97,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <IconButton onClick={handleBack}>
                <MdKeyboardArrowLeft
                  size={24}
                  color="#565656"
                  style={{ background: '' }}
                />
              </IconButton>
              <div>
                <Typography
                  variant="subtitle2"
                  style={{
                    marginLeft: '1rem'
                  }}
                >
                  {product?.product?.name || 'Sem nome'}
                </Typography>

                <Typography
                  variant="caption"
                  style={{
                    marginLeft: '1rem'
                  }}
                >
                  {product?.product?.id} - {product?.product?.reference}
                </Typography>
              </div>
            </div>
            <Divider />
          </div>

          <Typography
            variant="h6"
            style={{
              marginLeft: '1rem'
            }}
          >
            Configurador
          </Typography>

          <div style={{ padding: 12, paddingBottom: 100 }}>
            <FormProvider {...methods}>
              <ProductConfigurationReplicateForm
                onStartGenerateConfig={() => setIsLoading(true)}
                onEndGenerateConfig={() => setIsLoading(false)}
              />
            </FormProvider>
          </div>

          <div
            style={{
              position: `fixed`,
              width: DRAWER_WIDTH,
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 99
            }}
          >
            <Divider />
            <CardActions
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: 16
              }}
            >
              <div></div>
              <div>
                <Button type="button" variant="text" onClick={handleBack}>
                  Voltar
                </Button>
                <Button onClick={handleSubmit(onSubmit)} disabled={isError}>
                  Confirmar
                </Button>
              </div>
            </CardActions>
          </div>
        </Box>
      </Drawer>
    </>
  )
}
